// Live location from the browser (docs/standort-konzept.md), like the app's LiveLocations.kt, but
// only while the tab is open (a browser has no background service): positions encrypted with the
// share message's key, every 5 s at 5 m while someone watches, otherwise every 60 s at 25 m, a
// heartbeat every 5 minutes; at least 2.5 s apart (the server refuses closer ones).

import type { PositionResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import { ContentKind, decrypt, encrypt } from "../crypto/contentCipher.ts";
import { decodeUtf8, encodeUtf8, fromBase64, toBase64 } from "../data/bytes.ts";

export interface Position {
  lat: number;
  lon: number;
  acc?: number;
  t: number;
}

export async function encryptPosition(p: Position, mck: Uint8Array<ArrayBuffer>): Promise<string> {
  return toBase64(await encrypt(ContentKind.LOCATION, encodeUtf8(JSON.stringify(p)), mck));
}

export async function decryptPosition(base64: string, mck: Uint8Array<ArrayBuffer>): Promise<Position | null> {
  try {
    return JSON.parse(decodeUtf8(await decrypt(ContentKind.LOCATION, fromBase64(base64), mck))) as Position;
  } catch {
    return null;
  }
}

function meters(a: Position, b: Position): number {
  const r = 6_371_000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(x));
}

export class LiveShare {
  private seq = 0;
  private last: Position | null = null;
  private lastSent = 0;
  private watching = 0;
  private watchId: number | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private latest: Position | null = null;
  onEnd: () => void = () => undefined;
  /** A position went out (the sender's own card shows it). */
  onSent: (p: Position) => void = () => undefined;
  readonly messageId: string;
  private readonly mck: Uint8Array<ArrayBuffer>;
  private readonly session: Session;

  constructor(session: Session, messageId: string, mck: Uint8Array<ArrayBuffer>) {
    this.session = session;
    this.messageId = messageId;
    this.mck = mck;
  }

  start() {
    this.watchId = navigator.geolocation.watchPosition(
      (p) => {
        this.latest = { lat: p.coords.latitude, lon: p.coords.longitude, acc: Math.round(p.coords.accuracy), t: p.timestamp };
        void this.maybeSend(false);
      },
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
    this.heartbeat = setInterval(() => void this.maybeSend(true), 5 * 60_000);
  }

  /** Someone opened the map ({"t":"location_watch"}): a position now. */
  wake() {
    this.watching = Math.max(1, this.watching);
    void this.maybeSend(true);
  }

  async stop() {
    this.release();
    await this.session.call(`/v1/locations/${this.messageId}/stop`, { method: "POST" }).catch(() => undefined);
  }

  private async maybeSend(force: boolean) {
    const p = this.latest;
    if (!p) return;
    const now = Date.now();
    const interval = this.watching > 0 ? 5_000 : 60_000;
    const distance = this.watching > 0 ? 5 : 25;
    if (now - this.lastSent < 2_500) return;
    if (!force && this.last && (now - this.lastSent < interval || meters(this.last, p) < distance)) return;
    this.lastSent = now;
    this.last = p;
    try {
      const r = await this.session.call<PositionResponse>(`/v1/locations/${this.messageId}/position`, { method: "POST", json: { seq: ++this.seq, position: await encryptPosition(p, this.mck), track: false } });
      this.watching = r.watching;
      this.onSent(p);
      if (!r.active) {
        this.release();
        this.onEnd();
      }
    } catch {
      // The next position tries again.
    }
  }

  private release() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.watchId = null;
    this.heartbeat = null;
  }
}
